/**
 * OAuth HTTP service (CR-SSO-INVITE-PX-2026-09-26) — the SPA's side of inbound
 * SSO: which providers this environment offers, where a sign-in starts, how an
 * invite sign-in is started, and how the callback's one-time code is redeemed.
 *
 * Adapted from xelerate's `features/auth/services/oauthService.http.ts`, with one
 * PX rule on top: every call goes through the ONE transport (`apiClient`), so
 * errors stay `ApiError`-shaped and `backendKey` survives to the page.
 *
 * Wire contracts (server `OAuthController`):
 *  - `GET  /auth/oauth/providers` → `{providers: string[]}` (configured only).
 *  - `GET  /auth/oauth/{p}/authorize` → 302 to the provider (a top-level
 *    navigation, never an XHR) + the `px_oauth_state` browser-binding cookie.
 *  - `POST /auth/oauth/{p}/authorize` `{inviteToken}` → `{authorizeUrl}` + the
 *    same cookie. The token travels in this body ONLY (D5).
 *  - `POST /auth/oauth/exchange` `{code}` → `{accessToken, provider}`.
 */

import { apiClient, getApiBaseUrl } from '@/services'

/** The providers PX offers for SSO. GitHub exists server-side and is never offered. */
export type OAuthProviderId = 'google' | 'microsoft'

/** Display order, and the allowlist the server's list is filtered through (ruling 2026-09-26). */
export const OFFERED_PROVIDERS: readonly OAuthProviderId[] = ['google', 'microsoft']

interface ProvidersResponse {
  providers?: unknown
}

interface AuthorizeResponse {
  authorizeUrl?: unknown
}

interface ExchangeResponse {
  accessToken?: unknown
}

/**
 * The providers this environment can sign someone in with, in OFFERED order —
 * never in the server's order, and never a provider PX does not offer.
 */
export async function loadConfiguredProviders(): Promise<OAuthProviderId[]> {
  const response = await apiClient.get<ProvidersResponse>('/auth/oauth/providers')
  const listed = Array.isArray(response.providers) ? response.providers : []
  return OFFERED_PROVIDERS.filter((provider) => listed.includes(provider))
}

/** Where a plain `/login` sign-in starts: the server redirects on to the provider. */
export function loginAuthorizeUrl(provider: OAuthProviderId): string {
  return `${getApiBaseUrl().replace(/\/$/, '')}/auth/oauth/${provider}/authorize`
}

/**
 * Start an SSO sign-in for an invite: the token goes in the POST body, the
 * server parks it in its own state entry and answers with the provider URL
 * (which carries no token).
 */
export async function startInviteSso(provider: OAuthProviderId, inviteToken: string): Promise<string> {
  const response = await apiClient.post<AuthorizeResponse>(`/auth/oauth/${provider}/authorize`, {
    inviteToken
  })
  if (typeof response.authorizeUrl !== 'string' || response.authorizeUrl === '') {
    throw new Error('authorize response carried no authorizeUrl')
  }
  return response.authorizeUrl
}

/** Redeem the callback's single-use code for the access token. */
export async function redeemExchangeCode(code: string): Promise<string> {
  const response = await apiClient.post<ExchangeResponse>('/auth/oauth/exchange', { code })
  if (typeof response.accessToken !== 'string' || response.accessToken === '') {
    throw new Error('exchange response carried no accessToken')
  }
  return response.accessToken
}
